import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildVarianceRequest,
  getOnTimeVariance,
  VarianceApiError,
  VarianceHierarchyGrain,
  VarianceResponseData,
} from '../services/completedVarianceApi';
import type { AppliedFiltersSnapshot } from '../OnTimeDispatchDashboard';

const DEFAULT_TIMEOUT_MS = 65000; // slightly above Apigee 60s gateway timeout
const DEBOUNCE_MS = 100;

export interface UseOnTimeVarianceReturn {
  data: VarianceResponseData | null;
  loading: boolean;
  error: string | null;
  noData: boolean;
  message: string | null;
  refetch: () => void;
}

// Deliberately separate from useTrend: this chart is hierarchy-keyed, not time-based, and never re-fetches on timeframe changes.
export function useOnTimeVariance(
  appliedFilters: AppliedFiltersSnapshot,
  hierarchyGrain: VarianceHierarchyGrain
): UseOnTimeVarianceReturn {
  const [data, setData] = useState<VarianceResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggerCount, setTriggerCount] = useState(0);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setTriggerCount((prev) => prev + 1);
  }, []);

  const requestPayload = useMemo(
    () => buildVarianceRequest(appliedFilters, hierarchyGrain),
    [appliedFilters, hierarchyGrain]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await getOnTimeVariance(requestPayload, {
          timeoutMs: DEFAULT_TIMEOUT_MS,
          signal: controller.signal,
        });

        if (!isMountedRef.current || requestIdRef.current !== requestId) return;
        setData(response.data);
      } catch (err: any) {
        if (!isMountedRef.current || requestIdRef.current !== requestId) return;
        if (controller.signal.aborted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;

        const message =
          err instanceof VarianceApiError || err instanceof Error
            ? err.message
            : 'Failed to load On-Time Variance data';
        setData(null);
        setError(message);
      } finally {
        if (isMountedRef.current && requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [requestPayload, triggerCount]);

  return {
    data,
    loading,
    error,
    noData: !!data?.noData,
    message: data?.message ?? null,
    refetch,
  };
}
